import {Component, OnDestroy} from '@angular/core';
import {filter, Subject, takeUntil} from "rxjs";
import {
  groupStatusColors,
  indicatorColors,
  roles,
  roleUrlMap,
  sortOrderMap,
} from '../../../../shared/constants/common.constants';
import {ActivatedRoute, Router} from "@angular/router";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../../../state/app.state";
import {
  drillDownUrls,
  profileData,
  selectedUserProfile
} from "../../../../shared/core/shared.selectors";
import {CoachGroupsListService} from "../../service/coach-groups-list.service";
import {FetchGroupsListRequest} from "../../core/coach-groups-list.actions";
import {groupsList} from "../../core/coach-groups-list.selectors";
import {
  DeleteGroupRequest,
  SetDuplicateGroupRequest
} from "../../../group-details/core/group-details.actions";
import {GroupsList} from "../../../../../models/groups-list";
import {CommonService} from '../../../../shared/services/common.service';
import {Sort} from "@angular/material/sort";
import {SetDrillDownUrls} from "../../../../shared/core/shared.actions";
import {MatTableDataSource} from "@angular/material/table";

@Component({
  selector: 'app-coach-groups-list-table',
  templateUrl: './coach-groups-list-table.component.html',
  styleUrls: ['./coach-groups-list-table.component.css']
})
export class CoachGroupsListTableComponent implements OnDestroy {
  loading: boolean = false;
  coachGroupsListFilters = null;
  typeMap = {
    1: 'Participant',
    2: 'Adult',
  };
  groupListData: GroupsList;
  displayedColumns = [
    'name',
    'coachRole',
    'date',
    'type',
    'status',
    'participantcount',
    'currentSession',
    'upcomingSession',
    'totalAttendance',
    'actionMenu'
  ];

  sortOrder: any = -1;
  sortColumn: any = 'startDate';
  actionMenu = [
    {
      displayName: 'Duplicate',
      icon: 'edit',
      highLightColor: '',
      handleClick: (data) => this.handleDuplicateOption(data)
    },
    {
      displayName: 'Delete',
      icon: 'delete',
      highLightColor: '',
      handleClick: (data) => this.handleDeleteOption(data),
      status: [1]
    }
  ]
  role: number;
  roles = roles;
  isFirstPage = true;
  selectedUserId: number | null;
  selectedUserRole: number | null;
  tableData: any[];
  dataSource: any = new MatTableDataSource();
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private router: Router,
    private coachGroupsListService: CoachGroupsListService,
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private route: ActivatedRoute
  ) {

    this.store.select(profileData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.role = data.profile?.role;
      });

    this.store.select(selectedUserProfile)
      .pipe(
        filter((data) => !!data.profile),
        takeUntil(this.onDestroy)
      )
      .subscribe(data => {
        this.selectedUserRole = data.profile?.role;
      })

    if (this.role !== roles.superAdmin || this.selectedUserRole !== roles.superAdmin) {
      this.fetchGroupsList();
    }

    this.store.select(groupsList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.loading = data.loading;
        this.groupListData = data;

        if (!data.loading) {
          this.dataSource.data = [...data.list];

          if (data.hasNext) {
            setTimeout(() => {
              if (this.sortColumn && data.pageNumber === 1) {
                this.commonService.scrollColumnInView(this.sortColumn);
              }
            }, 0)

            setTimeout(() => {
              // check window height for infinite scroll
              this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
                this.onScroll();
              });
            }, 0)
          }
        }
      });


    this.coachGroupsListService.onGroupFilterChange
      .pipe(takeUntil(this.onDestroy))
      .subscribe((filters) => {
        this.coachGroupsListFilters = filters;
        this.isFirstPage = true;
        this.fetchGroupsList();
      });
  }

  fetchGroupsList(pageNumber = 1, limit = 10) {
    const params: any = this.route.snapshot.params;

    this.selectedUserId = params?.clientAdminId ||
                          params?.coachId ||
                          params?.memberId;

    this.store.dispatch(FetchGroupsListRequest({
      payload: {
        ...this.coachGroupsListFilters,
        pageNumber,
        limit,
        ...(this.sortOrder ? {sortOrder: this.sortOrder} : {}),
        ...(this.sortOrder ? {sortColumn: this.sortColumn} : {}),
        ...(this.selectedUserId ? {forUserId: this.selectedUserId} : {})
      }
    }));
  }

  onScroll() {
    if (this.groupListData?.hasNext && !this.groupListData?.loading) {
      this.fetchGroupsList(this.groupListData.pageNumber + 1, this.groupListData.limit);
      this.isFirstPage = false;
    }
  }


  handleDuplicateOption(group) {
    const payload = {
      groupId: group.id,
    };

    this.store.dispatch(SetDuplicateGroupRequest({payload}));
  }

  handleDeleteOption(group) {
    const payload = {
      groupId: group.id,
      isActive: false,
      refresh: true
    };
    this.store.dispatch(DeleteGroupRequest({payload}));
  }

  sortData(sort: Sort) {
    this.isFirstPage = true;
    this.sortColumn = `${sort.active}`;
    this.sortOrder = sortOrderMap[sort.direction];

    this.fetchGroupsList();
  }

  groupNameClick(data) {
    if (data?.id) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({payload: updatedDrillDownUrls}));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];
      this.router.navigate([`/${urlPrefix}/group/${data.id}/dashboard`]);

    }
  }

  sessionNameClick(item: any) {
    if (item?.id) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({payload: updatedDrillDownUrls}));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];
      this.router.navigate([`/${urlPrefix}/group/${item.id}/sessions/${item.lastSessionId}/details`]);
    }
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  protected readonly groupStatusColors = groupStatusColors;
}
